import { Control, FieldErrors } from 'react-hook-form';
import { Option, makeStyles, tokens } from '@fluentui/react-components';

import { ControlledField } from '@shared/components';

import { MOCK_DOCUMENT_LABELS, MOCK_PRODUCT_NAMES, MOCK_SOURCE_RECORDS } from '../../data';
import { QMS_AREA_OPTIONS, SAFETY_CONCERN_OPTIONS, URGENCY_OPTIONS } from '../../constants';
import { SAFETY_CONCERN } from '../../models';
import { TRequestFormData } from '../../validation';
import { RadioField, SectionCard } from '../shared';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr' },
  },
  full: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
    maxWidth: '720px',
  },
});

const SOURCE_OPTIONS = MOCK_SOURCE_RECORDS.map((record) => ({
  value: record.id,
  label: `${record.reference} · ${record.title}`,
}));
const sourceLabel = (id: string): string => SOURCE_OPTIONS.find((option) => option.value === id)?.label ?? '';

const identity = (value: string): string => value;

type TProps = {
  control: Control<TRequestFormData>;
  errors: FieldErrors<TRequestFormData>;
  readOnly: boolean;
  safetyConcern: string;
};

export const InitialScopeSection = ({ control, errors, readOnly, safetyConcern }: TProps) => {
  const classes = useStyles();
  return (
    <SectionCard
      title="Initial scope"
      description="Give what you know. You do not have to identify every affected document."
      collapsible
    >
      <div className={classes.grid}>
        <ControlledField
          name="affectedProducts"
          control={control}
          errors={errors}
          inputType="dropdown"
          label="Affected products"
          readonly={readOnly}
          multiselect
          getLabel={identity}
          placeholder="Select products"
        >
          {MOCK_PRODUCT_NAMES.map((product) => (
            <Option key={product} value={product} text={product}>
              {product}
            </Option>
          ))}
        </ControlledField>

        <ControlledField
          name="affectedQmsAreas"
          control={control}
          errors={errors}
          inputType="dropdown"
          label="Affected QMS areas"
          readonly={readOnly}
          multiselect
          getLabel={identity}
          placeholder="Select QMS areas"
        >
          {QMS_AREA_OPTIONS.map((area) => (
            <Option key={area} value={area} text={area}>
              {area}
            </Option>
          ))}
        </ControlledField>

        <ControlledField
          name="urgency"
          control={control}
          errors={errors}
          inputType="dropdown"
          label="Urgency"
          readonly={readOnly}
          getLabel={identity}
          placeholder="Select urgency"
        >
          {URGENCY_OPTIONS.map((urgency) => (
            <Option key={urgency} value={urgency} text={urgency}>
              {urgency}
            </Option>
          ))}
        </ControlledField>

        <ControlledField
          name="sourceRecord"
          control={control}
          errors={errors}
          inputType="dropdown"
          label="Source record"
          readonly={readOnly}
          getLabel={sourceLabel}
          placeholder="Link a source record"
        >
          {SOURCE_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value} text={option.label}>
              {option.label}
            </Option>
          ))}
        </ControlledField>

        <ControlledField
          name="knownAffectedDocuments"
          control={control}
          errors={errors}
          inputType="dropdown"
          label="Known affected documents"
          readonly={readOnly}
          multiselect
          getLabel={identity}
          placeholder="Select documents"
        >
          {MOCK_DOCUMENT_LABELS.map((document) => (
            <Option key={document} value={document} text={document}>
              {document}
            </Option>
          ))}
        </ControlledField>
      </div>

      <div className={classes.full}>
        <RadioField
          control={control}
          errors={errors}
          name="knownSafetyConcern"
          label="Is there a known safety concern?"
          disabled={readOnly}
          options={SAFETY_CONCERN_OPTIONS.map((value) => ({ value, label: value }))}
        />
        {safetyConcern === SAFETY_CONCERN.YES ? (
          <ControlledField
            name="safetyConcernDescription"
            control={control}
            errors={errors}
            inputType="textarea"
            label="Describe the safety concern"
            readonly={readOnly}
            resize="vertical"
            placeholder="What is the concern?"
          />
        ) : null}
      </div>
    </SectionCard>
  );
};
